import { useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios, { endpoints } from 'src/utils/axios';
//
import { AuthContext } from './auth-context';
import { isValidToken, setSession } from './utils';
import { ActionMapType, AuthStateType, AuthUserType } from '../../types';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  user: null,
  loading: true,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';


const createToken = (user: any) => {
  const base64UrlEncode = (str: string) => window.btoa(str)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

  const header = {
    alg: 'HS256',
    typ: 'JWT'
  };

  const payload = {
    userId: user.id,
    email: user.email,
    exp: Math.floor(Date.now() / 1000) + (3 * 24 * 60 * 60)
  };

  const encodedHeader = base64UrlEncode(JSON.stringify(header));
  const encodedPayload = base64UrlEncode(JSON.stringify(payload));
  const signature = base64UrlEncode('your-secret-key');

  return `${encodedHeader}.${encodedPayload}.${signature}`;
};


type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = sessionStorage.getItem(STORAGE_KEY);
      console.log('Access token:', accessToken);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);
        console.log('Token validated successfully');

        try {
          const response = await axios.get(endpoints.auth.me);
          console.log('User data response:', response);

          const user = response.data;
          console.log('User data:', user);

          dispatch({
            type: Types.INITIAL,
            payload: {
              user,
            },
          });
        } catch (error) {
          console.error('Error fetching user data:', error);

          // If there's a redirect, log the location
          if (error.response && error.response.status === 302) {
            console.error('Redirect location:', error.response.headers.location);
          }

          // Clear the session if we can't fetch user data
          setSession(null);

          dispatch({
            type: Types.INITIAL,
            payload: {
              user: null,
            },
          });
        }
      } else {
        console.log('No valid token found');
        dispatch({
          type: Types.INITIAL,
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      console.error('Initialization error:', error);
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: null,
        },
      });
    }
  }, []);
  console.log(state)

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email: string, password: string) => {
    try {
      const data = {
        username: email, // Using email as username as per the backend API
        password,
      };

      // Call the backend API for authentication
      const response = await axios.post(endpoints.auth.login, data);

      // Extract data from response according to AuthenticationResponseDTO schema
      const { token, refreshToken, userId, username, email: userEmail, displayName, role } = response.data;

      // Use token as accessToken
      setSession(token);

      // Create user object from response data
      const user = {
        id: userId,
        email: userEmail || email,
        username,
        displayName,
        role,
      };

      dispatch({
        type: Types.LOGIN,
        payload: {
          user,
        },
      });

    } catch (error) {

      console.error('Authentication failed:', error);
      throw error;
    }
  }, []);

  // REGISTER
  const register = useCallback(
    async (email: string, password: string, firstName: string, lastName: string) => {
      const data = {
        email,
        password,
        firstName,
        lastName,
      };

      const response = await axios.post(endpoints.auth.register, data);

      const { accessToken, user } = response.data;

      sessionStorage.setItem(STORAGE_KEY, accessToken);

      dispatch({
        type: Types.REGISTER,
        payload: {
          user,
        },
      });
    },
    []
  );

  // GOOGLE LOGIN
  const loginWithGoogle = useCallback(async () => {
    try {
      // Get the current URL for the redirect
      const redirectUri = `${window.location.origin}/oauth2/redirect`;

      // Get the Google OAuth2 URL from the backend
      const response = await axios.get(`${endpoints.auth.googleLogin}?redirectUri=${encodeURIComponent(redirectUri)}`);
      console.log(response.data)
      // Redirect to the Google OAuth2 URL
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Google authentication failed:', error);
      throw error;
    }
  }, []);

  // FACEBOOK LOGIN
  const loginWithFacebook = useCallback(async () => {
    try {
      // Get the current URL for the redirect
      const redirectUri = `${window.location.origin}/oauth2/redirect`;

      // Get the Facebook OAuth2 URL from the backend
      const response = await axios.get(`${endpoints.auth.facebookLogin}?redirectUri=${encodeURIComponent(redirectUri)}`);
      console.log(response.data)
      // Redirect to the Facebook OAuth2 URL
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Facebook authentication failed:', error);
      throw error;
    }
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    setSession(null);
    dispatch({
      type: Types.LOGOUT,
    });
  }, []);

  // ----------------------------------------------------------------------

  console.log(state)
  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      register,
      logout,
      loginWithGoogle,
      loginWithFacebook,
      initialize,
    }),
    [login, logout, register, loginWithGoogle, loginWithFacebook, initialize, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
